import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import { Dialog } from '@headlessui/react';
import validators from '@/utils/validators';
import FormErrors from '@/forms/_common/FormErrors';
import ErrorMessage from '@/forms/_common/ErrorMessage';
import DialogTemplate from '@/dialogs_common/DialogTemplate';
import Form from '@/dialogs/tagNode.create/Form';
import DESCRIPTION_MAX_LENGTH from '@/dialogs/tagNode.create/DESCRIPTION_MAX_LENGTH';
import utils from '@/dialogs/tagNode.create/utils';

TagNodeUpdateDialog.propTypes = {
  tag: PropTypes.object.isRequired,
  tagNode: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default function TagNodeUpdateDialog(props) {
  const { tag, tagNode, onSave, onCancel } = props;

  const [data, setData] = useState({
    description: tagNode.data.description ?? ''
  });
  const [showErrors, setShowErrors] = useState(false);

  const isRequired = utils.isRequired(tag);
  const prompts = utils.prompts(tag);

  // Store onCancel in a ref so the popstate listener always calls the latest version.
  const onCancelRef = useRef(onCancel);
  useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);

  // Push a dummy history entry on open so the back button closes the modal
  // instead of navigating away. Button-driven closes remove the listener
  // before popping, preventing the popstate handler from double-firing.
  const listenerRef = useRef(null);

  useEffect(() => {
    window.history.pushState({ modal: 'TagNodeUpdateDialog' }, '');
    listenerRef.current = () => onCancelRef.current();
    window.addEventListener('popstate', listenerRef.current);
    return () => window.removeEventListener('popstate', listenerRef.current);
  }, []);

  function closeWith(callback) {
    window.removeEventListener('popstate', listenerRef.current);
    window.history.back();
    callback();
  }

  function onSubmit() {
    closeWith(() => onSave(data.description));
  }

  function handleCancel() {
    closeWith(onCancel);
  }

  return ReactDOM.createPortal(
    <Dialog open onClose={handleCancel} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center">
        <Dialog.Panel className="w-screen max-w-md">
          <DialogTemplate>
            <FormErrors data={data} validators={{
              description: isRequired ? [
                validators.isRequired,
                validators.maxLength(DESCRIPTION_MAX_LENGTH)
              ] : [
                validators.maxLength(DESCRIPTION_MAX_LENGTH)
              ]
            }}>
              {({ errors, hasError }) => (
                <Form
                  icon={prompts.icon}
                  title={prompts.title}
                  subtitle={prompts.subtitle}
                  label={`${prompts.label}${isRequired ? '*' : ''}`}
                  placeholder={prompts.placeholder}
                  data={data}
                  errors={errors}
                  showErrors={showErrors}
                  onChange={(name, value) => {
                    setData(_.merge({}, data, { [name]: value }));
                  }}
                  onSubmit={() => hasError ? setShowErrors(true) : onSubmit()}
                  onCancel={handleCancel}
                  alert={showErrors ? (
                    <ErrorMessage>
                      Please fill in all required fields.
                    </ErrorMessage>
                  ) : null}
                />
              )}
            </FormErrors>
          </DialogTemplate>
        </Dialog.Panel>
      </div>
    </Dialog>,
    document.body
  );
}
